import { z } from 'zod';
import { ufSchema } from './cidades.ts';
import {
  dataHoraSchema,
  paginacaoSchema,
  regimeTributarioOpcionalSchema,
  regimeTributarioSchema,
  respostaListaSchema,
  semRegimeDePessoaFisica,
  tipoPessoaOuNaoInformadoSchema,
  uuidSchema,
} from './comum.ts';
import { contatoRespostaSchema, contatoSchema } from './contato.ts';
import { ehDocumentoValido } from './documento.ts';
import { enderecoSchema, textoOpcional } from './endereco.ts';

/**
 * Cliente é uma linha de `pessoas` com `is_cliente = true` (.spec/016).
 *
 * Gêmeo do contrato de fornecedor, e de propósito: é o mesmo cadastro de
 * pessoa, com outra flag de papel. Documento opcional, tipo e regime podendo
 * ficar em aberto — o cliente costuma entrar no sistema junto com a proposta,
 * antes de a documentação do contrato chegar.
 *
 * A diferença em relação a fornecedor é só qual papel viaja no payload: aqui
 * `isFornecedor` é o papel adicional, e `isEmpresa` continua sendo o campo que
 * a API só aceita de quem tem permissão sobre empresa.
 *
 * O cliente é quem figura em `contratos.cliente_id` e em contas a receber.
 */

const camposCliente = {
  tipo: tipoPessoaOuNaoInformadoSchema,
  nome: z.string().trim().min(2, 'Informe o nome ou a razão social.').max(200),
  /** Em PF repete o conteúdo de `nome`; o service preenche quando vier vazio. */
  nomeFantasia: z.string().trim().max(200).optional(),
  /** Vazio é aceito e vira `null`; preenchido, o dígito verificador é conferido. */
  documento: z.string().trim().max(20).prefault(''),
  regimeTributario: regimeTributarioOpcionalSchema,
  /**
   * Promove o cliente a empresa do próprio tenant.
   *
   * Gravar `true` aqui **exige permissão sobre empresa** — a checagem é da
   * rota, não do schema: o contrato descreve formato, não autorização.
   */
  isEmpresa: z.boolean().prefault(false),
  isFornecedor: z.boolean().prefault(false),
  observacao: textoOpcional(2000),
  ativo: z.boolean().prefault(true),
};

/**
 * Documento vazio passa; preenchido, precisa ser válido.
 *
 * Conferir só o tamanho deixaria `111.111.111-11` entrar no cadastro e virar
 * problema no dia de emitir o documento do contrato, quando já há valor
 * amarrado.
 *
 * Com o tipo `NI` não existe documento a conferir: não se sabe sequer qual dos
 * dois algoritmos aplicar. O mesmo `CHECK` existe no banco
 * (`pessoas_nao_informado_sem_documento`).
 */
function validarDocumento(
  dados: { tipo: 'PF' | 'PJ' | 'NI'; documento: string },
  ctx: z.RefinementCtx,
): void {
  if (dados.tipo === 'NI') {
    if (dados.documento.length === 0) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['documento'],
      message: 'Escolha Física ou Jurídica para informar o documento.',
    });
    return;
  }

  if (dados.documento.length === 0) return;
  if (ehDocumentoValido(dados.tipo, dados.documento)) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['documento'],
    message: dados.tipo === 'PF' ? 'CPF inválido.' : 'CNPJ inválido.',
  });
}

/**
 * Empresa do grupo é **pessoa jurídica**, e com regime informado.
 *
 * Tipo e regime são opcionais no cliente e obrigatórios na empresa, e este
 * cadastro promove um no outro. A segunda regra também é `CHECK` no banco
 * (`pessoas_empresa_exige_regime`); aqui ela existe para o erro chegar preso ao
 * campo, e não como violação de constraint traduzida em 500.
 *
 * A primeira **passou a exigir `PJ`**, e antes aceitava `PF` — o banco ainda
 * aceita (`pessoas_empresa_exige_tipo` só barra `NI`). O que mudou foi a regra
 * do regime: pessoa física não tem regime tributário, e o campo some da tela
 * quando o tipo é `PF`. Sem esta exigência, marcar como empresa um cliente
 * físico produziria a única combinação sem saída do cadastro — a empresa precisa
 * de regime, o físico não tem onde informá-lo, e o `CHECK` do banco recusaria a
 * gravação com um 500 sobre um campo invisível.
 *
 * A checagem do regime não roda quando o tipo já falhou: dois erros sobre a
 * mesma decisão mandariam o usuário a dois campos para corrigir uma coisa só.
 */
function validarPapelEmpresa(
  dados: {
    tipo: 'PF' | 'PJ' | 'NI';
    regimeTributario: 'NORMAL' | 'SIMPLES' | 'MEI' | null;
    isEmpresa: boolean;
  },
  ctx: z.RefinementCtx,
): void {
  if (!dados.isEmpresa) return;

  if (dados.tipo !== 'PJ') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['tipo'],
      message:
        'Empresa do grupo é pessoa jurídica: é ela que emite documento fiscal e tem regime tributário.',
    });
    return;
  }

  // É o regime da empresa que decide o direito a crédito de ICMS, PIS e COFINS
  // na entrada da nota. Sem ele, a apuração encontraria `NULL` no meio da
  // confirmação.
  if (dados.regimeTributario === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['regimeTributario'],
      message: 'Empresa do grupo precisa ter o regime tributário informado.',
    });
  }
}

/**
 * A transformação vem **depois** das duas conferências, e é ela que zera o
 * regime da pessoa física — ver `semRegimeDePessoaFisica`. Nesta ordem porque
 * `validarPapelEmpresa` precisa enxergar o que o usuário mandou: invertida, o
 * cliente físico promovido a empresa passaria pela conferência com o regime que
 * digitou e chegaria ao banco sem nenhum.
 */
export const criarClienteSchema = z
  .object({ ...camposCliente })
  .merge(enderecoSchema)
  // E-mail, telefone e inscrição municipal: o tomador da nota fiscal de serviço
  // (`.spec/015`, decisão 88). Opcionais como o resto — ver `contato.ts`.
  .merge(contatoSchema)
  .superRefine(validarDocumento)
  .superRefine(validarPapelEmpresa)
  .transform(semRegimeDePessoaFisica);

export type CriarCliente = z.infer<typeof criarClienteSchema>;

export const atualizarClienteSchema = criarClienteSchema;
export type AtualizarCliente = z.infer<typeof atualizarClienteSchema>;

/**
 * Resposta.
 *
 * `documento` volta apenas com dígitos; a máscara é aplicada na exibição por
 * `formatDocumento`, nunca gravada.
 */
export const clienteSchema = z
  .object({
    id: uuidSchema,
    tipo: tipoPessoaOuNaoInformadoSchema,
    nome: z.string(),
    nomeFantasia: z.string(),
    documento: z.string().nullable(),
    regimeTributario: regimeTributarioSchema.nullable(),
    isEmpresa: z.boolean(),
    isFornecedor: z.boolean(),
    observacao: z.string().nullable(),
    ativo: z.boolean(),
    createdAt: dataHoraSchema,
    updatedAt: dataHoraSchema,
  })
  .merge(
    enderecoSchema.extend({
      /** Desnormalizado na leitura para a listagem não pedir a cidade à parte. */
      cidadeNome: z.string().nullable(),
      cidadeUf: ufSchema.nullable(),
    }),
  )
  .merge(contatoRespostaSchema);

export type Cliente = z.infer<typeof clienteSchema>;

export const listaClientesSchema = respostaListaSchema(clienteSchema);
export type ListaClientes = z.infer<typeof listaClientesSchema>;

export const listarClientesQuerySchema = paginacaoSchema.extend({
  /** Ausente traz ativos e inativos; a listagem mostra ativos por padrão. */
  ativo: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
});

export type ListarClientesQuery = z.infer<typeof listarClientesQuerySchema>;

/**
 * `nomeDeUso` é a ordem **padrão**, e não `nome`.
 *
 * O cliente passou a ser exibido pelo nome fantasia, com a razão social de queda
 * (ver `nomeDoCliente`, na web, e `nomeDeUso`, na API) — a mesma decisão já
 * tomada no fornecedor, e pelo mesmo motivo: é assim que ele é chamado dentro da
 * construtora. Ordenar por `nome` enquanto a lista mostra o fantasia produz uma
 * sequência que se lê como fora de ordem, e o usuário conclui que a listagem
 * está com defeito, não que há duas colunas.
 *
 * As duas colunas cruas continuam ordenáveis: quem confere cadastro às vezes
 * quer a razão social pura.
 */
export const COLUNAS_ORDENAVEIS_CLIENTE = [
  'nomeDeUso',
  'nome',
  'nomeFantasia',
  'createdAt',
] as const;
